import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Form,
  Input,
  Select,
  InputNumber,
  Tabs,
  Tag,
  Space,
  Typography,
  Table,
  Alert,
  Empty,
} from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  CloudUploadOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Button, Card, Modal, Badge } from "../../components/Common";
import { useMutations } from "../../hooks/useApiMutation";
import { useConfirm } from "../../hooks/useConfirm";

const { Text, Title, Paragraph } = Typography;

const STATUS_VARIANT = {
  Draft: "neutral",
  Published: "success",
  Archived: "warning",
};

/**
 * Detail view for an agent the client owns. Mirrors the back-office agent screen but
 * only exposes what a tenant may change — no company picker, no global publishing.
 */
const PortalAgentDetail = () => {
  const { id } = useParams();
  const agentId = Number(id);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { mutate, loading } = useMutations();
  const { confirmAction } = useConfirm();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  const [agent, setAgent] = useState(null);
  const [models, setModels] = useState([]);
  const [keys, setKeys] = useState([]);
  const [keyModalOpen, setKeyModalOpen] = useState(false);
  const [issuedKey, setIssuedKey] = useState(null);
  const [keyForm] = Form.useForm();

  const loadAgent = useCallback(async () => {
    await mutateRef.current("portalAgent", {
      method: "get",
      endpoint: `/portal/agents/${agentId}`,
      onSuccess: (data) => {
        setAgent(data);
        form.setFieldsValue({
          name: data?.name,
          description: data?.description,
          provider: data?.provider,
          model: data?.model,
          systemPrompt: data?.systemPrompt,
          temperature: data?.temperature,
          maxTokens: data?.maxTokens,
          topP: data?.topP,
        });
      },
    });
  }, [agentId, form]);

  const loadModels = useCallback(async (provider) => {
    await mutateRef.current("portalModels", {
      method: "get",
      endpoint: "/portal/llm-models",
      params: provider ? { provider } : {},
      onSuccess: (data) => setModels(data || []),
      onError: () => setModels([]),
    });
  }, []);

  const loadKeys = useCallback(async () => {
    await mutateRef.current("portalAgentKeys", {
      method: "get",
      endpoint: `/portal/agents/${agentId}/keys`,
      onSuccess: (data) => setKeys(data || []),
      onError: () => setKeys([]),
    });
  }, [agentId]);

  useEffect(() => {
    if (!agentId) return;
    loadAgent();
    loadKeys();
  }, [agentId, loadAgent, loadKeys]);

  useEffect(() => {
    if (agent?.provider) loadModels(agent.provider);
  }, [agent?.provider, loadModels]);

  const onSave = async () => {
    const values = await form.validateFields();
    await mutate("portalUpdateAgent", {
      method: "put",
      endpoint: `/portal/agents/${agentId}`,
      payload: values,
      onSuccess: () => loadAgent(),
    });
  };

  const onLifecycle = async (action) => {
    await mutate(`portalAgent_${action}`, {
      method: "post",
      endpoint: `/portal/agents/${agentId}/${action}`,
      onSuccess: () => loadAgent(),
    });
  };

  const onCreateKey = async () => {
    const values = await keyForm.validateFields();
    await mutate("portalCreateKey", {
      method: "post",
      endpoint: `/portal/agents/${agentId}/keys`,
      payload: { label: values.label, environment: values.environment, scopes: "chat" },
      onSuccess: (data) => {
        setIssuedKey(data);
        setKeyModalOpen(false);
        keyForm.resetFields();
        loadKeys();
      },
    });
  };

  const keyColumns = [
    { title: "Label", dataIndex: "label", key: "label", render: (v) => v || "—" },
    { title: "Key", dataIndex: "keyPrefix", key: "keyPrefix", render: (v) => <Text code>{v}…</Text> },
    {
      title: "Environment",
      dataIndex: "environment",
      key: "environment",
      render: (v) => <Tag color={v === "live" ? "green" : "gold"}>{v}</Tag>,
    },
    {
      title: "Last used",
      dataIndex: "lastUsedAt",
      key: "lastUsedAt",
      render: (v) => (v ? dayjs(v).format("DD MMM YYYY HH:mm") : "—"),
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) => (
        <Badge variant={r.active ? "success" : "error"}>
          {r.active ? "Active" : "Revoked"}
        </Badge>
      ),
    },
    {
      title: "",
      key: "actions",
      render: (_, r) =>
        r.active ? (
          <Button
            variant="ghost"
            danger
            icon={<DeleteOutlined />}
            onClick={() =>
              confirmAction({
                key: "portalRevokeKey",
                method: "delete",
                endpoint: `/portal/agents/${agentId}/keys/${r.agentApiKeyId}`,
                title: "Revoke key",
                content:
                  "Any integration still using this key stops working immediately. Continue?",
                okText: "Revoke",
                okType: "danger",
                onSuccess: loadKeys,
              })
            }
          />
        ) : null,
    },
  ];

  const configurationTab = (
    <Card>
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Space style={{ width: "100%" }} align="start" size="large" wrap>
          <Form.Item name="provider" label="Provider" rules={[{ required: true }]}>
            <Select
              style={{ width: 200 }}
              onChange={(p) => {
                form.setFieldValue("model", undefined);
                loadModels(p);
              }}
            >
              <Select.Option value="OpenAI">OpenAI</Select.Option>
              <Select.Option value="Claude">Claude</Select.Option>
              <Select.Option value="Vision">Google Vision</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="model" label="Model">
            <Select
              style={{ width: 280 }}
              allowClear
              showSearch
              optionFilterProp="label"
              options={models.map((m) => ({
                value: m.modelId,
                label: `${m.displayName} — ${m.modelId}`,
              }))}
            />
          </Form.Item>
        </Space>
        <Form.Item name="systemPrompt" label="System prompt (training)">
          <Input.TextArea rows={10} />
        </Form.Item>
        <Space size="large" wrap>
          <Form.Item name="temperature" label="Temperature">
            <InputNumber min={0} max={2} step={0.1} />
          </Form.Item>
          <Form.Item name="maxTokens" label="Max tokens">
            <InputNumber min={1} step={100} />
          </Form.Item>
          <Form.Item name="topP" label="Top P">
            <InputNumber min={0} max={1} step={0.05} />
          </Form.Item>
        </Space>
        <Button
          variant="primary"
          icon={<SaveOutlined />}
          loading={loading?.portalUpdateAgent}
          onClick={onSave}
        >
          Save changes
        </Button>
      </Form>
    </Card>
  );

  const keysTab = (
    <Card
      title="API keys"
      extra={
        <Button variant="primary" icon={<PlusOutlined />} onClick={() => setKeyModalOpen(true)}>
          New key
        </Button>
      }
    >
      {issuedKey && (
        <Alert
          type="success"
          showIcon
          closable
          onClose={() => setIssuedKey(null)}
          style={{ marginBottom: 16 }}
          message="Copy this key now"
          description={
            <>
              <Paragraph copyable={{ text: issuedKey.rawKey }} style={{ marginBottom: 4 }}>
                <Text code>{issuedKey.rawKey}</Text>
              </Paragraph>
              <Text type="secondary">
                It is stored only as a hash — this is the one and only time it is shown.
              </Text>
            </>
          }
        />
      )}

      {keys.length ? (
        <Table
          rowKey="agentApiKeyId"
          columns={keyColumns}
          dataSource={keys}
          pagination={false}
          size="small"
        />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No keys yet. Publish the agent, then issue a key to call it."
        />
      )}
    </Card>
  );

  return (
    <Space direction="vertical" size={20} style={{ width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <Button
          variant="ghost"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/portal")}
        />
        <div style={{ flex: 1, minWidth: 200 }}>
          <Text style={{ fontSize: 12, color: "var(--text-muted)" }}>Agent</Text>
          <Title level={3} style={{ margin: 0 }}>
            {agent?.name || "Loading…"}
          </Title>
        </div>
        <Space>
          {agent?.status && (
            <Badge variant={STATUS_VARIANT[agent.status] || "neutral"}>{agent.status}</Badge>
          )}
          <Button
            variant="secondary"
            icon={<CloudUploadOutlined />}
            loading={loading?.portalAgent_publish}
            disabled={agent?.status === "Published"}
            onClick={() => onLifecycle("publish")}
          >
            Publish
          </Button>
          <Button
            variant="secondary"
            icon={<InboxOutlined />}
            loading={loading?.portalAgent_archive}
            disabled={agent?.status === "Archived"}
            onClick={() => onLifecycle("archive")}
          >
            Archive
          </Button>
        </Space>
      </div>

      <Tabs
        items={[
          { key: "config", label: "Configuration", children: configurationTab },
          { key: "keys", label: "Keys", children: keysTab },
        ]}
      />

      <Modal
        open={keyModalOpen}
        onClose={() => setKeyModalOpen(false)}
        onConfirm={onCreateKey}
        title="Issue an API key"
        confirmText="Create key"
        loading={loading?.portalCreateKey}
      >
        <Form form={keyForm} layout="vertical" initialValues={{ environment: "live" }}>
          <Form.Item name="label" label="Label" rules={[{ required: true }]}>
            <Input placeholder="e.g. Production backend" />
          </Form.Item>
          <Form.Item name="environment" label="Environment">
            <Select>
              <Select.Option value="live">Live — billed</Select.Option>
              <Select.Option value="test">Test — not billed</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default PortalAgentDetail;
